/**
 * Вход в вилку, известный ЗАРАНЕЕ: точный адрес каждой ноги и сумма в купоне.
 *
 * Почему заранее, а не по нажатию. Браузер открывает вторую вкладку только из
 * обработчика самого нажатия: стоит дождаться внутри него хоть одного ответа
 * сети — и всплывающее окно блокируется. Значит спросить сервер по клику нельзя
 * в принципе, и остаётся одно — спросить до клика, пока человек читает строку.
 *
 * Спрашиваем две ручки сразу и параллельно:
 *
 * * `/where` — точный адрес события, **только если** в ноге ещё раздел или
 *   пусто. На V1 адаптеры часто уже отдают event URL (Фонбет, Леон, после
 *   яруса −2 — BetBoom и Cloudbet). Сервер в этом случае не стучит в площадку
 *   (ярус −1). Гость получает 402, не 404: доступ — Telegram ACCESS.
 * * `/ticket` — суммы, посчитанные по шагу ставки площадки, и купон исхода с
 *   уже вбитой суммой там, где площадка это умеет.
 *
 * Побеждает самый точный адрес из трёх известных (лента, купон, адресация), а
 * не последний пришедший: купон исхода точнее страницы матча, страница матча
 * точнее раздела. Сравнение идёт по объявленному уровню, а не по длине строки.
 *
 * Ответ живёт недолго и намеренно: вилка живёт 10–15 секунд, и адрес, добытый
 * минуту назад, ведёт на матч, которого в ленте уже нет.
 */

import { create } from 'zustand';

import { apiUrl } from '../lib/build';

/** Лестница точности входа. Больше — ближе к форме ставки. */
const RANK: Record<string, number> = { section: 0, event: 1, outcome: 2 };

const rankOf = (precision: string | undefined): number => RANK[precision ?? 'section'] ?? 0;

/** Сколько считаем добытый вход годным, мс. */
export const FRESH_MS = 20_000;

export interface EntryLeg {
  url: string;
  precision: string;
  /** Сумма ноги в валюте площадки, строкой: деньги через float теряют копейку. */
  stake?: string;
  currency?: string;
  /** Нашла ли адресация событие. `false` — адрес прежний, матч искать самому. */
  located: boolean;
}

export interface Entry {
  id: string;
  /**
   * Отпечаток вилки, под который добыт купон. Нет или пусто — спросили без
   * сверки: такой ответ живёт, пока `keep` не назовёт живой.
   */
  mark?: string;
  /** По коду площадки: ноги одной вилки на одной площадке не бывает. */
  legs: Record<string, EntryLeg>;
  at: number;
}

interface Wire {
  legs?: { s?: string; url?: string | null; p?: string; stake?: string; currency?: string }[];
}

interface WhereWire {
  legs?: { s?: string; url?: string | null; p?: string; found?: boolean }[];
}

interface EntryState {
  known: Map<string, Entry>;
  /** Идёт ли запрос по этой вилке: два подряд не нужны. */
  asking: Set<string>;
  /** Живой отпечаток: купон с другим mark в форму не подставляют. */
  live: Map<string, string>;
  /** `bank` — рубли, `fx` — рублей за доллар: сервер считает купон в долларах. */
  warm: (id: string, bank: number, fx: number, mark?: string) => void;
  /** Запомнить, чем сейчас ставят. Чужой купон сразу забывается. */
  keep: (id: string, mark: string) => void;
  forget: (id: string) => void;
}

async function ask(id: string, bank: string): Promise<Entry> {
  const [ticket, located] = await Promise.all([
    fetch(apiUrl(`/v1/fork/${encodeURIComponent(id)}/ticket?bank=${bank}`), {
      credentials: 'same-origin',
    })
      .then((res) => (res.ok ? (res.json() as Promise<Wire>) : null))
      .catch(() => null),
    fetch(apiUrl(`/v1/fork/${encodeURIComponent(id)}/where`), { credentials: 'same-origin' })
      .then((res) => (res.ok ? (res.json() as Promise<WhereWire>) : null))
      .catch(() => null),
  ]);

  const legs: Record<string, EntryLeg> = {};
  const better = (source: string, url: string | null | undefined, precision: string | undefined, found: boolean) => {
    if (typeof url !== 'string' || url === '') return;
    const known = legs[source];
    if (known !== undefined && rankOf(known.precision) >= rankOf(precision)) {
      // Адрес хуже уже известного не заменяет его, но факт находки сохраняем:
      // человеку важно знать, что матч опознан, даже если купон был точнее.
      if (found) known.located = true;
      return;
    }
    legs[source] = { url, precision: precision ?? 'section', located: found };
  };

  for (const leg of ticket?.legs ?? []) {
    if (leg.s == null) continue;
    better(leg.s, leg.url, leg.p, false);
    const mine = legs[leg.s];
    if (mine !== undefined) {
      if (leg.stake != null) mine.stake = leg.stake;
      if (leg.currency != null) mine.currency = leg.currency;
    }
  }
  for (const leg of located?.legs ?? []) {
    if (leg.s == null) continue;
    better(leg.s, leg.url, leg.p, leg.found === true);
  }
  return { id, mark: '', legs, at: Date.now() };
}

export const useEntry = create<EntryState>((set, get) => ({
  known: new Map(),
  asking: new Set(),
  live: new Map(),

  keep: (id, mark) => {
    const had = get().known.get(id);
    const same = get().live.get(id) === mark;
    if (same && (had === undefined || (had.mark ?? '') === mark)) return;
    const live = new Map(get().live);
    live.set(id, mark);
    if (had !== undefined && (had.mark ?? '') !== mark) {
      const known = new Map(get().known);
      known.delete(id);
      set({ live, known });
      return;
    }
    set({ live });
  },

  warm: (id, bank, fx, mark = '') => {
    if (mark !== '') get().keep(id, mark);
    const state = get();
    const had = state.known.get(id);
    if (
      had !== undefined &&
      Date.now() - had.at < FRESH_MS &&
      (mark === '' || (had.mark ?? '') === mark)
    ) {
      return;
    }
    if (state.asking.has(id) && (mark === '' || state.live.get(id) === mark)) return;
    // Банк на экране рублёвый, купон считается в долларах (BETSLIP_BANK_CURRENCY).
    // Отправить рубли как доллары значило бы попросить ставку в сто раз крупнее
    // задуманной — поэтому без курса не спрашиваем вовсе.
    if (!(fx > 0) || !(bank > 0)) return;
    set({ asking: new Set(state.asking).add(id) });
    const asked = mark;
    void ask(id, (bank / fx).toFixed(2))
      .then((entry) => {
        const wanted = get().live.get(id);
        // Ответ на вчерашний кэф не перезаписывает уже добытый сегодняшний.
        if (asked !== '' && wanted !== undefined && wanted !== asked) return;
        const known = new Map(get().known);
        known.set(id, { ...entry, mark: asked });
        set({ known });
      })
      .catch(() => {
        // Молча: вход остаётся тем, что дала лента. Ручка адресации — ускорение,
        // а не условие работы.
      })
      .finally(() => {
        const asking = new Set(get().asking);
        asking.delete(id);
        set({ asking });
      });
  },

  forget: (id) => {
    const known = new Map(get().known);
    known.delete(id);
    const live = new Map(get().live);
    live.delete(id);
    set({ known, live });
  },
}));

/** Что известно про ногу прямо сейчас. `null` — ничего лучше ленты нет. */
export function entryLeg(id: string, source: string): EntryLeg | null {
  const state = useEntry.getState();
  const entry = state.known.get(id);
  if (entry === undefined || Date.now() - entry.at > FRESH_MS) return null;
  const live = state.live.get(id);
  // Купон с другим отпечатком — мёртвый тикет: адрес и сумма от вчерашнего кэфа.
  if (live !== undefined && (entry.mark ?? '') !== live) return null;
  return entry.legs[source] ?? null;
}
